// File: StepOptions
// Created: 2026-05-12
// Updated: 2026-05-29
// Author: Erick Hernández Silva

import React from 'react';
import type { Option, Step } from './legacyTypes';

interface Props {
  currentStep: Step;
  currentStepIndex: number;
  selectedOption: Option | null;
  selectedOptions: { [stepIndex: number]: string };
  selectedSubOptions: { [stepIndex: number]: string };
  step14VariantMode: 'black' | 'matching';
  handleOptionChange: (stepIndex: number, optionId: string) => void;
  handleSubOptionChange: (stepIndex: number, subOptionId: string) => void;
  setStep14VariantMode: (mode: 'black' | 'matching') => void;
  setStep14SubOptionByName: (name: string | null) => void;
  getStep12InteriorColorName: () => string | null;
  contrastSteeringName?: string | null;
  headerText?: string;
  disabled?: boolean;
}

const StepOptions: React.FC<Props> = ({
  currentStep,
  currentStepIndex,
  selectedOption,
  selectedOptions,
  selectedSubOptions,
  step14VariantMode,
  handleOptionChange,
  handleSubOptionChange,
  setStep14VariantMode,
  setStep14SubOptionByName,
  getStep12InteriorColorName,
  contrastSteeringName,
  headerText = 'SELECT OPTION',
  disabled = false,
}) => {
  const contrastLabel = contrastSteeringName || 'Black';
  const allOptionsHaveNoImages = currentStep.options.every(opt => {
    const presentationOk = !!opt.presentationImage && opt.presentationImage.startsWith('http');
    const anyViewOk = Object.values(opt.images || {}).some(v => typeof v === 'string' && v.startsWith('http'));
    return !presentationOk && !anyViewOk;
  });
  const shouldShowAsTextOnly = currentStep.isTextOnly || allOptionsHaveNoImages;
  const shouldShowOptionDescriptions = currentStep.stepNumber === 1 || currentStep.stepNumber === 2;

  return (
    <>
      <div className="mb-4">
        <h3 className="text-sm font-bold tracking-wider text-gray-800 mb-3">{headerText}</h3>

        {currentStep.stepNumber === 15 ? (
          (() => {
            const selectedMainId = selectedOptions[currentStepIndex];
            const originalOpt = currentStep.options.find(o => o.optionName?.toLowerCase() === 'original');
            const nardiOpt = currentStep.options.find(o => o.optionName?.toLowerCase() === 'nardi');

            const isOriginalSelected = !!originalOpt && selectedMainId === originalOpt.optionId;
            const isNardiSelected = !!nardiOpt && selectedMainId === nardiOpt.optionId;

            return (
              <div className="grid grid-cols-1 gap-y-3">
                {originalOpt && (
                  <label className={`flex items-start gap-3 p-3 border-b border-gray-200 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                    <input
                      type="radio"
                      name={`step-${currentStepIndex}`}
                      checked={isOriginalSelected}
                      disabled={disabled}
                      onChange={() => {
                        if (disabled) return;
                        handleOptionChange(currentStepIndex, originalOpt.optionId);
                        // Keep current variant mode; default to black if nothing selected
                        if (step14VariantMode === 'black') {
                          setStep14SubOptionByName(contrastLabel);
                        } else {
                          setStep14SubOptionByName(getStep12InteriorColorName());
                        }
                      }}
                      className="mt-1 custom-radio"
                    />
                    <div className="flex-1">
                      <div className="text-base font-medium text-gray-800">Original</div>
                    </div>
                  </label>
                )}

                {isOriginalSelected && (
                  <div className="pl-8 -mt-1">
                    <div className="text-xs font-bold tracking-wider text-gray-600 mb-2">SELECT VARIANT</div>
                    <div className="space-y-2">
                      <label className={`flex items-start gap-3 py-1 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                        <input
                          type="radio"
                          name={`step-${currentStepIndex}-variant`}
                          checked={step14VariantMode === 'black'}
                          disabled={disabled}
                          onChange={() => {
                            if (disabled) return;
                            setStep14VariantMode('black');
                            setStep14SubOptionByName(contrastLabel);
                          }}
                          className="mt-1 custom-radio"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-800">{contrastLabel}</div>
                        </div>
                      </label>

                      <label className={`flex items-start gap-3 py-1 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                        <input
                          type="radio"
                          name={`step-${currentStepIndex}-variant`}
                          checked={step14VariantMode === 'matching'}
                          disabled={disabled}
                          onChange={() => {
                            if (disabled) return;
                            setStep14VariantMode('matching');
                            setStep14SubOptionByName(getStep12InteriorColorName());
                          }}
                          className="mt-1 custom-radio"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-800">Match interior color</div>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {nardiOpt && (
                  <label className={`flex items-start gap-3 p-3 border-b border-gray-200 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                    <input
                      type="radio"
                      name={`step-${currentStepIndex}`}
                      checked={isNardiSelected}
                      disabled={disabled}
                      onChange={() => {
                        if (disabled) return;
                        handleOptionChange(currentStepIndex, nardiOpt.optionId);
                      }}
                      className="mt-1 custom-radio"
                    />
                    <div className="flex-1">
                      <div className="text-base font-medium text-gray-800">Nardi</div>
                    </div>
                  </label>
                )}
              </div>
            );
          })()
        ) : (
          <div className={`grid gap-y-3.5 gap-x-2 ${shouldShowAsTextOnly ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {currentStep.options.map((option) => {
              const isSelected = selectedOptions[currentStepIndex] === option.optionId;
              const previewImage = option.presentationImage && option.presentationImage.startsWith('http')
                ? option.presentationImage
                : option.images.view1;

              return (
                <label
                  key={option.optionId}
                  className={`cursor-pointer transition-all duration-200 ${
                    shouldShowAsTextOnly ? 'flex items-start gap-3 p-3 border-b border-gray-200 last:border-b-0' : ''
                  }`}
                >
                  {shouldShowAsTextOnly && (
                    <input
                      type="radio"
                      name={`step-${currentStepIndex}`}
                      checked={isSelected}
                      disabled={disabled}
                      onChange={() => !disabled && handleOptionChange(currentStepIndex, option.optionId)}
                      className="mt-1 custom-radio"
                    />
                  )}
                  <div
                    onClick={() => !disabled && !shouldShowAsTextOnly && handleOptionChange(currentStepIndex, option.optionId)}
                    className={`flex-1 ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                  >
                    {!shouldShowAsTextOnly ? (
                      previewImage && previewImage.startsWith('http') ? (
                        <div className="relative overflow-hidden transition-all duration-200 hover:shadow-lg aspect-video">
                          <img
                            src={previewImage}
                            alt={option.optionName}
                            className="w-full h-full object-contain"
                          />
                          {isSelected && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <img
                                src="https://cdn.prod.website-files.com/68472be536d16d9004a489e5/69c2f77e872fffc7542078c5_IcOutlineCheck.png"
                                alt="Selected"
                                className="w-4 h-4"
                                style={{ filter: 'invert(1)' }}
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="relative overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 aspect-video flex flex-col items-center justify-center p-4">
                          <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                          <p className="text-xs text-gray-500 text-center">No image available</p>
                          {isSelected && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <img
                                src="https://cdn.prod.website-files.com/68472be536d16d9004a489e5/69c2f77e872fffc7542078c5_IcOutlineCheck.png"
                                alt="Selected"
                                className="w-4 h-4"
                                style={{ filter: 'invert(1)' }}
                              />
                            </div>
                          )}
                        </div>
                      )
                    ) : null}

                    <div className={shouldShowAsTextOnly ? '' : 'mt-2'}>
                      <div className="text-sm font-medium text-gray-900">{option.optionName}</div>
                      {shouldShowOptionDescriptions && option.optionDescription && (
                        <div className="text-sm font-normal text-gray-500 mt-1">{option.optionDescription}</div>
                      )}
                      {option.optionPrice !== 0 && (
                        <div className="text-sm font-normal text-gray-600 mt-1">
                          {option.optionPrice > 0 ? '+' : ''}
                          {option.optionPrice.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            maximumFractionDigits: 0,
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {currentStep.stepNumber !== 15 && currentStep.stepNumber !== 12 && selectedOption?.subOptions && selectedOption.subOptions.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-bold tracking-wider text-gray-600 mb-4">SELECT VARIANT</h3>
          <div className="grid grid-cols-2 gap-3">
            {selectedOption.subOptions.map((subOption) => {
              const isSelected = selectedSubOptions[currentStepIndex] === subOption.optionId;

              return (
                <div
                  key={subOption.optionId}
                  onClick={() => handleSubOptionChange(currentStepIndex, subOption.optionId)}
                  className="cursor-pointer transform transition-transform duration-200 hover:scale-105"
                >
                  {subOption.images.view1 && subOption.images.view1.startsWith('http') ? (
                    <div className="relative rounded-lg overflow-hidden transition-all duration-200 hover:shadow-lg">
                      <img
                        src={subOption.images.view1}
                        alt={subOption.optionName}
                        className="w-full h-auto object-contain"
                      />
                      {isSelected && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <img
                            src="https://cdn.prod.website-files.com/68472be536d16d9004a489e5/69c2f77e872fffc7542078c5_IcOutlineCheck.png"
                            alt="Selected"
                            className="w-4 h-4"
                            style={{ filter: 'invert(1)' }}
                          />
                        </div>
                      )}
                    </div>
                  ) : null}
                  <div className="mt-2">
                    <div className="text-sm font-medium text-gray-800">{subOption.optionName}</div>
                    {subOption.optionPrice !== 0 && (
                      <div className="text-xs text-gray-500">
                        {subOption.optionPrice > 0 ? '+' : ''}
                        {subOption.optionPrice.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          maximumFractionDigits: 0,
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default StepOptions;
